import { useRef, useState } from 'react';
import { useClickAway } from 'ahooks';

import { Box, Input, Text } from '@zillow/constellation-10';

import { autocompleteContainerStyles, dropdownItemStyles, dropdownItemTextStyles, dropdownStyles } from './styles';

import type React from 'react';

type AutocompleteItem = {
  name: string;
};

type Props = {
  className: string;
  disabled?: boolean;
  items?: Array<AutocompleteItem>;
  onChange: (s: string) => void;
  onSelect: (item: AutocompleteItem) => void;
  placeholder?: string;
  value?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
};
const Autocomplete = ({
  className,
  disabled = false,
  items = [],
  onChange,
  onSelect,
  placeholder = 'Search',
  value = '',
  inputRef,
}: Props) => {
  const ref = useRef(null);
  const [isFocused, toggleFocus] = useState(false);
  const [activeIndex, updateActiveIndex] = useState(-1);

  useClickAway(() => toggleFocus(false), ref);

  const handleFocus = () => {
    toggleFocus(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleSelect = (item: AutocompleteItem) => {
    onSelect(item);
    toggleFocus(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const keyCode = e.key;
    toggleFocus(true);

    if (keyCode === 'ArrowUp') {
      const upOneIndex = activeIndex === -1 ? items.length - 1 : activeIndex - 1;
      updateActiveIndex(upOneIndex);
      e.preventDefault();
    } else if (keyCode === 'ArrowDown') {
      const downOneIndex = activeIndex === items.length - 1 ? -1 : activeIndex + 1;
      updateActiveIndex(downOneIndex);
      e.preventDefault();
    } else if (keyCode === 'Escape') {
      toggleFocus(false);
    } else if (keyCode === 'Tab') {
      toggleFocus(false);
    } else if (keyCode === 'Enter') {
      e.preventDefault();
      // if no activeIndex, default to first selection
      const selectedItem = activeIndex === -1 ? items[0] : items[activeIndex];
      if (selectedItem) {
        handleSelect(selectedItem);

        if (e.target instanceof HTMLElement) {
          e.target.blur();
        }
      } else {
        // user did not select an option
      }
    }
  };

  const renderDropdownItem = ({ idx, item }: { idx: number; item: AutocompleteItem }) => (
    <Box
      css={{ ...dropdownItemStyles, background: idx === activeIndex ? 'bg.subtle' : 'bg.neutral' }}
      data-testid="dropdown-item"
      key={item.name}
      role="option"
      aria-selected={idx === activeIndex}
      onClick={() => handleSelect(items[idx])}
      onMouseEnter={() => updateActiveIndex(idx)}
    >
      <Box css={dropdownItemTextStyles} key={item.name}>
        <Text>{item.name}</Text>
      </Box>
    </Box>
  );

  const renderDropdown = () => {
    if (!isFocused || !items.length) {
      return null;
    }
    return (
      <Box css={dropdownStyles} role="listbox">
        {items.map((item, idx) => renderDropdownItem({ item, idx }))}
      </Box>
    );
  };

  /**
   * Note about disabling Google Chrome's autocomplete:
   * autocomplete="off || false" prop on <input> should work, however Constellation's
   * Input does not pass `...rest` to the <input>. Luckily, you can disable
   * autocomplete for an entire form in the same way.
   * The 'data-' prefix used to embed custom attributes on HTML elements and fix typescript errors.
   */
  return (
    <Box css={autocompleteContainerStyles} className={className}>
      <form ref={ref} data-autocomplete="off">
        <Input
          data-testid="autocomplete"
          ref={inputRef}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          aria-expanded={isFocused}
          aria-autocomplete="list"
        />

        {renderDropdown()}
      </form>
    </Box>
  );
};

export default Autocomplete;
